// @ts-ignore
import braintreeClient from 'braintree-web/client';
// @ts-ignore
import hostedFields from 'braintree-web/hosted-fields';
// @ts-ignore
import threeDSecure from 'braintree-web/three-d-secure';
// @ts-ignore
import dataCollector from 'braintree-web/data-collector';

export interface ThreeDSecureVerifyResult {
    nonce: string;
    threeDSecureAuthenticationId: string | null;
    liabilityShifted: boolean | null;
    liabilityShiftPossible: boolean | null;
}

export interface HostedFieldsTokenizeResult {
    nonce: string;
    bin: string;
}

export function useBraintree() {
    async function initClient(clientToken: string): Promise<any> {
        return braintreeClient.create({ authorization: clientToken });
    }

    async function initHostedFields(clientInstance: any, fieldConfig: any): Promise<any> {
        return hostedFields.create({
            client: clientInstance,
            styles: {
                input: {
                    'font-size': '16px',
                    color: '#444',
                },
                'input.invalid': { color: '#e53935' },
                'input.valid': { color: '#2e7d32' },
            },
            fields: fieldConfig,
        });
    }

    async function initThreeDSecure(clientInstance: any): Promise<any> {
        return threeDSecure.create({ client: clientInstance, version: 2 });
    }

    async function initDataCollector(clientInstance: any): Promise<string> {
        const instance = await dataCollector.create({ client: clientInstance });
        return instance.deviceData as string;
    }

    async function tokenizeCard(hostedFieldsInstance: any): Promise<HostedFieldsTokenizeResult> {
        const payload = await hostedFieldsInstance.tokenize();
        return {
            nonce: payload.nonce,
            bin: payload.details.bin,
        };
    }

    async function tokenizeRawCard(
        clientInstance: any,
        card: { number: string; expirationDate: string; cvv: string; cardholderName?: string },
    ): Promise<HostedFieldsTokenizeResult> {
        const response = await clientInstance.request({
            endpoint: 'payment_methods/credit_cards',
            method: 'post',
            data: {
                creditCard: {
                    number: card.number.replace(/[^\d]/g, ''),
                    expirationDate: card.expirationDate,
                    cvv: card.cvv,
                    cardholderName: card.cardholderName || '',
                },
            },
        });
        const creditCard = response.creditCards[0];
        return {
            nonce: creditCard.nonce,
            bin: creditCard.details.bin,
        };
    }

    async function verify3DSecure(
        threeDSecureInstance: any,
        options: {
            nonce: string;
            bin: string;
            amount: string;
            email: string;
            cardholderName?: string;
            challengeRequested?: boolean;
            collectDeviceData?: boolean;
        },
    ): Promise<ThreeDSecureVerifyResult> {
        const response = await threeDSecureInstance.verifyCard({
            amount: options.amount,
            nonce: options.nonce,
            bin: options.bin,
            email: options.email,
            cardholderName: options.cardholderName || '',
            challengeRequested: options.challengeRequested !== false,
            collectDeviceData: options.collectDeviceData !== false,
            onLookupComplete: (_data: any, next: () => void) => {
                next();
            },
        });

        return {
            nonce: response.nonce,
            threeDSecureAuthenticationId: response.threeDSecureInfo?.threeDSecureAuthenticationId ?? null,
            liabilityShifted: response.threeDSecureInfo?.liabilityShifted ?? null,
            liabilityShiftPossible: response.threeDSecureInfo?.liabilityShiftPossible ?? null,
        };
    }

    /**
     * Moderate policy: accept if liability shifted or card is not enrolled (not possible to shift).
     * Reject only if authentication was attempted but failed.
     */
    function evaluate3DSResult(result: ThreeDSecureVerifyResult): { accepted: boolean; reason: string } {
        if (result.liabilityShifted === true) {
            return { accepted: true, reason: 'liability_shifted' };
        }
        if (result.liabilityShiftPossible === false) {
            // card not enrolled in 3DS — allow through
            return { accepted: true, reason: 'not_enrolled' };
        }
        // liability shift was possible but did not happen — auth failed
        return { accepted: false, reason: 'auth_failed' };
    }

    return {
        initClient,
        initHostedFields,
        initThreeDSecure,
        initDataCollector,
        tokenizeCard,
        tokenizeRawCard,
        verify3DSecure,
        evaluate3DSResult,
    };
}
